import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatDetail, fmtDate, fmtBytes } from "@/lib/api";
import { PageHeader, PrimaryButton, EmptyState, StatusBadge } from "@/components/ui-parts";
import { Upload, Loader2, FileSpreadsheet, Trash2, Eye, Download, Landmark, Pencil, Check, X, CheckCircle2, XCircle } from "lucide-react";
import { toast } from "sonner";
import { MonthYearPicker, currentMonthPrefix, isoStartsWithMonth } from "@/components/MonthYearPicker";

function AccountLabelInline({ acc, onSave }) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(acc.account_label || "");
  const save = async () => {
    await onSave(value);
    setEditing(false);
  };
  if (!editing) {
    return (
      <button onClick={() => setEditing(true)}
        data-testid={`edit-account-label-${(acc.iban || 'none').replace(/\s/g, '')}`}
        className="inline-flex items-center gap-1 text-xs text-[#5C5E60] hover:text-[#18322B] hover:underline transition-colors">
        {acc.account_label ? <span className="font-semibold text-[#1A1A1A]">{acc.account_label}</span> : <span className="italic">Konto benennen</span>}
        <Pencil className="h-3 w-3" />
      </button>
    );
  }
  return (
    <div className="inline-flex items-center gap-1">
      <input value={value} onChange={(e) => setValue(e.target.value)} placeholder="z.B. Girokonto Sparkasse"
        className="text-xs px-2 py-0.5 border border-[#E6E6E4] rounded" autoFocus onKeyDown={(e) => e.key === "Enter" && save()} />
      <button onClick={save} className="p-1 rounded hover:bg-[#E4F0E8] text-[#18322B]"><Check className="h-3.5 w-3.5" /></button>
      <button onClick={() => setEditing(false)} className="p-1 rounded hover:bg-[#F3F3F1] text-[#5C5E60]"><X className="h-3.5 w-3.5" /></button>
    </div>
  );
}

export default function Statements() {
  const [companies, setCompanies] = useState([]);
  const [items, setItems] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [companyId, setCompanyId] = useState("auto");
  const [accountLabel, setAccountLabel] = useState("");
  const [loading, setLoading] = useState(true);
  const [queue, setQueue] = useState([]);
  const [monthFilter, setMonthFilter] = useState(currentMonthPrefix());
  const fileRef = useRef(null);

  const loadCompanies = async () => {
    const { data } = await api.get("/companies");
    setCompanies(data);
    if (data.length && !companyId) setCompanyId("auto");
  };

  const load = async () => {
    setLoading(true);
    try {
      const [s, a] = await Promise.all([
        api.get("/statements"),
        api.get("/statements/accounts"),
      ]);
      setItems(Array.isArray(s.data) ? s.data : []);
      setAccounts(Array.isArray(a.data) ? a.data : []);
    } catch (err) {
      // Preserve whatever was on screen — never wipe the list because of a
      // transient error (Cloudflare 502, mongo blip, …). Only surface a toast
      // if we don't already have data, so background polls stay silent.
      setItems((prev) => {
        if (!prev || prev.length === 0) toast.error("Konnte Auszüge nicht laden");
        return prev;
      });
    }
    finally { setLoading(false); }
  };

  useEffect(() => { loadCompanies(); load(); }, []);

  // Poll every 5 s while any statement is still being analyzed in the
  // background so transaction counts, IBAN and status flip live from
  // "wird analysiert…" to the final numbers without a manual refresh.
  useEffect(() => {
    const hasPending = items.some((s) => s.status === "extracting");
    if (!hasPending) return;
    const t = setInterval(() => { load(); }, 5000);
    return () => clearInterval(t);
  }, [items]);

  const uploadOne = async (qItem) => {
    setQueue((q) => q.map((x) => x.id === qItem.id ? { ...x, status: "uploading", progress: 5 } : x));
    try {
      const fd = new FormData();
      fd.append("file", qItem.file, qItem.file.name);
      // "auto" = KI erkennt Firma anhand IBAN / Kontoinhaber
      if (companyId && companyId !== "auto") fd.append("company_id", companyId);
      if (accountLabel) fd.append("account_label", accountLabel);
      const { data } = await api.post("/statements/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (pe) => {
          if (pe.total) {
            const p = Math.min(90, Math.round((pe.loaded / pe.total) * 90));
            setQueue((q) => q.map((x) => x.id === qItem.id ? { ...x, progress: p } : x));
          }
        },
      });
      setQueue((q) => q.map((x) => x.id === qItem.id ? { ...x, status: "done", progress: 100, result: data } : x));
    } catch (err) {
      setQueue((q) => q.map((x) => x.id === qItem.id ? { ...x, status: "error", error: formatDetail(err.response?.data?.detail) || err.message } : x));
    }
  };

  const enqueueFiles = async (files) => {
    if (!files || files.length === 0) return;
    const newItems = Array.from(files).map((f) => ({
      id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      file: f, name: f.name, size: f.size, status: "queued", progress: 0,
    }));
    setQueue((q) => [...q, ...newItems]);
    for (const item of newItems) {
      await uploadOne(item);
    }
    // One toast for the whole batch instead of one per file.
    const okCount = newItems.filter((n) => queue.find((q) => q.id === n.id)?.status !== "error").length;
    if (newItems.length === 1) {
      toast.info("Auszug wird analysiert … Buchungen erscheinen gleich");
    } else if (okCount > 0) {
      toast.info(`${newItems.length} Auszüge werden nacheinander analysiert`);
    }
    load();
  };

  const clearQueue = () => setQueue([]);

  const remove = async (s) => {
    if (!window.confirm(`Auszug "${s.name}" löschen?`)) return;
    try {
      await api.delete(`/statements/${s.id}`);
      toast.success("Auszug gelöscht");
      load();
    } catch { toast.error("Löschen fehlgeschlagen"); }
  };

  const exportPdf = async (s) => {
    try {
      const res = await api.get(`/statements/${s.id}/export`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `vorbuch_${(s.name || "auszug").replace(/\s+/g, "_")}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("PDF-Export fehlgeschlagen"); }
  };

  const renameAccount = async (acc, newLabel) => {
    try {
      await api.patch("/statements/accounts/label", { company_id: acc.company_id, iban: acc.iban, account_label: newLabel });
      toast.success("Konto umbenannt");
      load();
    } catch { toast.error("Umbenennen fehlgeschlagen"); }
  };

  // Group statements by company_id → iban
  const filteredItems = React.useMemo(() => {
    if (!monthFilter) return items;
    return items.filter((s) => {
      // Match on the statement's own booking period. Fallback to the upload
      // month when the extraction hasn't finished yet or produced no period.
      return isoStartsWithMonth(s.period_end, monthFilter)
        || isoStartsWithMonth(s.period_start, monthFilter)
        || (!s.period_end && !s.period_start && isoStartsWithMonth(s.created_at, monthFilter));
    });
  }, [items, monthFilter]);

  const grouped = React.useMemo(() => {
    const map = {};
    accounts.forEach((a) => {
      const key = `${a.company_id}::${a.iban || "__none__"}`;
      map[key] = { account: a, statements: [] };
    });
    filteredItems.forEach((s) => {
      const key = `${s.company_id}::${s.iban || "__none__"}`;
      if (!map[key]) map[key] = {
        account: { company_id: s.company_id, iban: s.iban, account_holder: s.account_holder, account_label: s.account_label, count: 0 },
        statements: []
      };
      map[key].statements.push(s);
    });
    // Group again by company for nested rendering; drop empty accounts (from
    // the /accounts aggregation) so the UI only shows companies that actually
    // have statements in the selected month.
    const byCompany = {};
    Object.values(map).forEach(({ account, statements }) => {
      if (statements.length === 0) return;
      if (!byCompany[account.company_id]) byCompany[account.company_id] = [];
      byCompany[account.company_id].push({ account, statements });
    });
    return byCompany;
  }, [filteredItems, accounts]);

  return (
    <div>
      <PageHeader
        title="Kontoauszüge"
        subtitle="Mehrere Auszüge auf einmal hochladen – auch für Firmen mit mehreren Konten. vorbuch.de erkennt IBAN und gruppiert automatisch."
        actions={<MonthYearPicker value={monthFilter} onChange={setMonthFilter} testid="statements-month" />}
      />

      <div className="px-8 py-8 space-y-6">
        {companies.length === 0 ? (
          <EmptyState
            title="Zuerst eine Firma anlegen"
            hint="Ohne Firma können keine Kontoauszüge zugeordnet werden."
            action={<Link to="/app/firmen"><PrimaryButton>Firma anlegen</PrimaryButton></Link>}
          />
        ) : (
          <>
            <div className="bg-white border border-[#E6E6E4] rounded-md p-6" data-testid="upload-panel">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-xs font-semibold text-[#1A1A1A] mb-1.5">Firma</label>
                  <select data-testid="statement-company-select"
                    value={companyId} onChange={(e) => setCompanyId(e.target.value)}
                    className="w-full px-3 py-2 border border-[#E6E6E4] rounded-md text-sm focus:outline-none focus:border-[#18322B] focus:ring-1 focus:ring-[#18322B]">
                    <option value="auto">🤖 Automatisch erkennen (empfohlen)</option>
                    <optgroup label="Manuell zuweisen">
                      {companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                    </optgroup>
                  </select>
                  <div className="text-[11px] text-[#5C5E60] mt-1">
                    Bei &bdquo;Automatisch&ldquo; liest die KI IBAN + Kontoinhaber aus dem Auszug und wählt die Firma selbst.
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-[#1A1A1A] mb-1.5">Konto-Bezeichnung (optional)</label>
                  <input data-testid="statement-account-label"
                    value={accountLabel} onChange={(e) => setAccountLabel(e.target.value)}
                    placeholder="z.B. Girokonto Sparkasse"
                    className="w-full px-3 py-2 border border-[#E6E6E4] rounded-md text-sm focus:outline-none focus:border-[#18322B]" />
                  <div className="text-[11px] text-[#5C5E60] mt-1">Wird auf alle folgenden Uploads angewendet und bereits vorhandenen Auszügen desselben IBAN zugewiesen.</div>
                </div>
              </div>

              <div
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => { e.preventDefault(); enqueueFiles(e.dataTransfer.files); }}
                onClick={() => fileRef.current?.click()}
                className="border-2 border-dashed border-[#A0A0A0] bg-[#F9F9F8] rounded-lg p-10 flex flex-col items-center justify-center cursor-pointer transition-colors duration-150 hover:border-[#18322B] hover:bg-[#E4F0E8]"
                data-testid="statement-dropzone">
                <Upload className="h-8 w-8 text-[#5C5E60]" strokeWidth={1.5} />
                <div className="mt-3 text-sm text-[#1A1A1A] font-medium">Mehrere Kontoauszüge hierher ziehen oder klicken</div>
                <div className="text-xs text-[#5C5E60] mt-1">PDF oder CSV · IBAN wird automatisch erkannt und getrennt gruppiert</div>
                <input ref={fileRef} type="file" multiple accept=".pdf,.csv,application/pdf,text/csv"
                  className="hidden" onChange={(e) => enqueueFiles(e.target.files)}
                  data-testid="statement-file-input" />
              </div>

              {queue.length > 0 && (
                <div className="mt-4 border border-[#E6E6E4] rounded-md overflow-hidden" data-testid="upload-queue">
                  <div className="flex items-center justify-between px-4 py-2 bg-[#F9F9F8] border-b border-[#E6E6E4]">
                    <div className="text-xs font-semibold text-[#1A1A1A]">
                      {queue.filter((x) => x.status === "done").length} von {queue.length} verarbeitet
                    </div>
                    <button onClick={clearQueue} className="text-xs text-[#5C5E60] hover:underline">Liste leeren</button>
                  </div>
                  <div className="max-h-96 overflow-y-auto">
                    {queue.map((q) => (
                      <div key={q.id} className="flex items-center gap-3 px-4 py-3 border-b border-[#E6E6E4] last:border-b-0" data-testid={`queue-${q.id}`}>
                        <FileSpreadsheet className="h-5 w-5 text-[#5C5E60] shrink-0" />
                        <div className="flex-1 min-w-0">
                          <div className="text-sm text-[#1A1A1A] truncate">{q.name}</div>
                          <div className="text-xs text-[#5C5E60] flex items-center gap-2 flex-wrap">
                            <span>{fmtBytes(q.size)}</span>
                            {q.status === "uploading" && <span>· KI-Analyse ({q.progress}%)</span>}
                            {q.status === "done" && q.result && (
                              <>
                                <span>·</span>
                                <span>{q.result.transaction_count || 0} Buchungen erkannt</span>
                                {q.result.iban && <span className="font-mono">· {q.result.iban}</span>}
                                {q.result.auto_detected_company && (
                                  <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded bg-[#EAF4FA] text-[#1A4971]">
                                    🤖 {q.result.auto_detected_company.company_name} ({q.result.auto_detected_company.confidence === "high" ? "sicher" : "möglich"})
                                  </span>
                                )}
                              </>
                            )}
                            {q.status === "error" && <span className="text-[#B31D1D]">· {q.error}</span>}
                          </div>
                          <div className="mt-1 h-1 rounded-full bg-[#F3F3F1] overflow-hidden">
                            <div className={`h-full transition-all duration-300 ${q.status === "error" ? "bg-[#B31D1D]" : "bg-[#18322B]"}`}
                              style={{ width: `${q.progress}%` }} />
                          </div>
                        </div>
                        <div className="shrink-0">
                          {q.status === "done" && <CheckCircle2 className="h-5 w-5 text-[#1E4620]" strokeWidth={1.75} />}
                          {q.status === "error" && <XCircle className="h-5 w-5 text-[#B31D1D]" strokeWidth={1.75} />}
                          {q.status === "uploading" && <Loader2 className="h-5 w-5 text-[#18322B] animate-spin" />}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {loading ? (
              <div className="text-sm text-[#5C5E60]">Lade...</div>
            ) : filteredItems.length === 0 ? (
              items.length === 0 ? (
                <EmptyState title="Noch keine Kontoauszüge" hint="Laden Sie oben Ihre ersten Auszüge hoch." />
              ) : (
                <EmptyState
                  title="Keine Auszüge in diesem Zeitraum"
                  hint={monthFilter ? 'Wähle einen anderen Monat oder klicke auf „alle" für die gesamte Liste.' : ""}
                />
              )
            ) : (
              <div className="space-y-4">
                {companies.filter((c) => grouped[c.id]).map((c) => (
                  <div key={c.id} className="bg-white border border-[#E6E6E4] rounded-md overflow-hidden" data-testid={`company-block-${c.id}`}>
                    <div className="border-b border-[#E6E6E4] px-6 py-3 flex items-center gap-2 bg-[#F9F9F8]">
                      <span className="h-2.5 w-2.5 rounded-full" style={{ background: c.color }} />
                      <div className="font-display font-bold text-[#1A1A1A]">{c.name}</div>
                      <StatusBadge variant="neutral" testid={`company-accounts-count-${c.id}`}>
                        {grouped[c.id].length} {grouped[c.id].length === 1 ? "Konto" : "Konten"}
                      </StatusBadge>
                    </div>

                    {grouped[c.id].map(({ account, statements }) => (
                      <div key={account.iban || "__none__"} className="border-b border-[#E6E6E4] last:border-b-0">
                        <div className="px-6 py-3 bg-[#FBFBFA] flex items-center justify-between gap-4 flex-wrap">
                          <div className="flex items-center gap-2 min-w-0">
                            <Landmark className="h-4 w-4 text-[#5C5E60] shrink-0" />
                            <div className="min-w-0">
                              <div className="flex items-center gap-2 flex-wrap">
                                <AccountLabelInline acc={account} onSave={(v) => renameAccount(account, v)} />
                                <span className="text-xs text-[#5C5E60] font-mono">{account.iban || "kein IBAN erkannt"}</span>
                              </div>
                              {account.account_holder && <div className="text-xs text-[#5C5E60]">{account.account_holder}</div>}
                            </div>
                          </div>
                          <div className="text-xs text-[#5C5E60]">
                            {statements.length} {statements.length === 1 ? "Auszug" : "Auszüge"}
                            {account.latest_period_end && <> · zuletzt bis {fmtDate(account.latest_period_end)}</>}
                          </div>
                        </div>

                        <table className="w-full text-sm">
                          <thead>
                            <tr className="text-[10px] uppercase tracking-[0.15em] text-[#5C5E60] bg-white border-b border-[#E6E6E4]">
                              <th className="text-left px-6 py-2 font-semibold">Datei</th>
                              <th className="text-left px-4 py-2 font-semibold">Zeitraum</th>
                              <th className="text-right px-4 py-2 font-semibold">Buchungen</th>
                              <th className="text-right px-6 py-2 font-semibold">Aktionen</th>
                            </tr>
                          </thead>
                          <tbody>
                            {statements.map((s) => (
                              <tr key={s.id} className="border-b border-[#E6E6E4] last:border-b-0 hover:bg-[#F9F9F8] transition-colors duration-150">
                                <td className="px-6 py-2.5">
                                  <div className="flex items-center gap-2">
                                    <FileSpreadsheet className="h-4 w-4 text-[#5C5E60]" strokeWidth={1.75} />
                                    <div className="font-medium text-[#1A1A1A] truncate">{s.filename}</div>
                                  </div>
                                </td>
                                <td className="px-4 py-2.5 text-[#5C5E60]">
                                  {s.status === "extracting" ? (
                                    <span className="inline-flex items-center gap-1.5 text-[#915000]">
                                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                      Wird analysiert …
                                    </span>
                                  ) : s.status === "failed" ? (
                                    <span className="text-[#B31D1D]" title={s.failure_reason || "Analyse fehlgeschlagen"}>
                                      Analyse fehlgeschlagen
                                    </span>
                                  ) : s.period_start ? `${fmtDate(s.period_start)} – ${fmtDate(s.period_end)}` : "—"}
                                </td>
                                <td className="text-right px-4 py-2.5 font-mono-tab">
                                  {s.status === "extracting" ? (
                                    <StatusBadge variant="warning">…</StatusBadge>
                                  ) : (
                                    <div className="inline-flex flex-col items-end gap-0.5">
                                      <StatusBadge variant={s.transaction_count > 0 ? "success" : "warning"}>
                                        {s.transaction_count}
                                      </StatusBadge>
                                      {(s.open_receipt_count > 0 || s.open_contract_count > 0) && (
                                        <div className="text-[10px] text-[#5C5E60] leading-tight text-right">
                                          {s.open_receipt_count > 0 && (
                                            <div>
                                              <span className="text-[#B31D1D] font-semibold">{s.open_receipt_count}</span> ohne Beleg
                                            </div>
                                          )}
                                          {s.contract_candidate_count > 0 && (
                                            <div>
                                              <span className="text-[#915000] font-semibold">{s.contract_candidate_count}</span> Vertrags-Kandidat{s.contract_candidate_count === 1 ? "" : "en"}
                                              {s.open_contract_count > 0 && (
                                                <> · <span className="text-[#B31D1D] font-semibold">{s.open_contract_count}</span> ohne Vertrag</>
                                              )}
                                            </div>
                                          )}
                                        </div>
                                      )}
                                    </div>
                                  )}
                                </td>
                                <td className="text-right px-6 py-2.5">
                                  <Link to={`/app/kontoauszuege/${s.id}`}
                                    className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-md hover:bg-[#F3F3F1] text-[#1A1A1A] mr-1"
                                    data-testid={`view-statement-${s.id}`}>
                                    <Eye className="h-3.5 w-3.5" /> Öffnen
                                  </Link>
                                  <button onClick={() => exportPdf(s)}
                                    data-testid={`export-statement-${s.id}`}
                                    className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-md hover:bg-[#F3F3F1] text-[#1A1A1A] mr-1">
                                    <Download className="h-3.5 w-3.5" /> PDF
                                  </button>
                                  <button onClick={() => remove(s)}
                                    data-testid={`delete-statement-${s.id}`}
                                    className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-md hover:bg-[#FCE8E8] text-[#B31D1D]">
                                    <Trash2 className="h-3.5 w-3.5" />
                                  </button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
